import { Component } from '@theme/component';
import { fetchConfig, onAnimationEnd, preloadImage } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

export const ADD_TO_CART_TEXT_ANIMATION_DURATION = 2000;

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number | undefined} */
  #animationTimeout;

  /** @type {number | undefined} */
  #cleanupTimeout;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    this.animateAddToCart();

    // Fly-to-cart animation removed
    // if (!event.target.closest('.quick-add-modal')) this.#animateFlyToCart();
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the add to cart button.
   */
  animateAddToCart() {
    const { addToCartButton } = this.refs;

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);

    if (!addToCartButton.classList.contains('atc-added')) {
      addToCartButton.classList.add('atc-added');
    }

    this.#animationTimeout = setTimeout(() => {
      this.#cleanupTimeout = setTimeout(() => {
        this.refs.addToCartButton.classList.remove('atc-added');
      }, 10);
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
  }
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();
  #selectedUpsells = [];

  /** @type {number | undefined} */
  #timeout;

  connectedCallback() {
    // console.log('ProductFormComponent connected', this.id);
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    
    // Listen for upsell changes - the handler will only dispatch when selections exist
    document.addEventListener('upsells:change', this.#onUpsellsChange, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  #onUpsellsChange = (event) => {
    this.#selectedUpsells = event.detail.selectedUpsells || [];
    // console.log('Upsells changed event received:', this.#selectedUpsells);
  }

  /**
   * Handles the submit event for the product form.
   *
   * @param {Event} event - The submit event.
   */
  async handleSubmit(event) {
    // console.log('ProductFormComponent.handleSubmit called', this.id);
    const { addToCartTextError } = this.refs;
    // Stop default behaviour from the browser
    event.preventDefault();

    if (this.#timeout) clearTimeout(this.#timeout);

    // Check if the add to cart button is disabled and do an early return if it is
    if (this.refs.addToCartButtonContainer?.refs.addToCartButton?.getAttribute('disabled') === 'true') return;

    // Send the add to cart information to the cart
    const form = this.querySelector('form');

    if (!form) throw new Error('Product form element missing');

    const formData = new FormData(form);

    // Check for HSA/Subscription conflicts
    const productId = this.dataset.productId;
    const variantId = formData.get('id');

    if (window.productVisibilityManager) {
      const conflictCheck = await window.productVisibilityManager.hasConflict(productId, variantId);
      if (conflictCheck.hasConflict) {
        // Show error message
        if (addToCartTextError) {
          addToCartTextError.classList.remove('hidden');
          addToCartTextError.style.removeProperty('display');
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = conflictCheck.message;
          } else {
            const newTextNode = document.createTextNode(conflictCheck.message);
            addToCartTextError.appendChild(newTextNode);
          }
          this.#setLiveRegionText(conflictCheck.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');
            addToCartTextError.style.display = 'none';
            this.#clearLiveRegionText();
          }, 5000);
        }
        return;
      }
    }
    
    // Build items array with main product and any selected upsells
    const items = [];
    
    // Add main product
    const mainProductId = formData.get('id');
    const mainProductQuantity = formData.get('quantity') || 1;
    const mainProductItem = {
      id: parseInt(mainProductId),
      quantity: parseInt(mainProductQuantity)
    };

    // Check for subscription selling plan
    const sellingPlanId = formData.get('selling_plan');
    if (sellingPlanId) {
      mainProductItem.selling_plan = parseInt(sellingPlanId);
    }

    items.push(mainProductItem);

    // Add selected upsells
    // console.log('Checking for upsells to add:', this.#selectedUpsells);
    if (this.#selectedUpsells.length > 0) {
      // console.log('Adding upsells to cart:', this.#selectedUpsells);
      items.push(...this.#selectedUpsells);
    } else {
      // console.log('No upsells selected');
    }

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    });

    // Use JSON body with items array for multiple products
    const body = {
      items: items,
      sections: cartItemComponentsSectionIds.join(',')
    };

    const fetchCfg = fetchConfig('javascript', { body: JSON.stringify(body) });

    // console.log('Sending to cart API:', body);

    // Create an AbortController for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

    fetch(Theme.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      body: JSON.stringify(body),
      signal: controller.signal
    })
      .then((response) => {
        clearTimeout(timeoutId);
        // console.log('Cart API response status:', response.status);

        // Check if response is ok
        if (!response.ok && response.status !== 422) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        // Safely parse JSON
        return response.json().catch(() => {
          throw new Error('Invalid JSON response from server');
        });
      })
      .then((response) => {
        // console.log('Cart API response data:', response);

        if (response.status) {
          // console.error('Cart API error:', response.message);
          window.dispatchEvent(new CartErrorEvent(this.id, response.message));

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');
          addToCartTextError.style.removeProperty('display');

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');
            addToCartTextError.style.display = 'none';

            // Clear the announcement
            this.#clearLiveRegionText();
          }, 10000);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: items.reduce((sum, item) => sum + item.quantity, 0),
              productId: this.dataset.productId,
            })
          );

          return;
        } else {
          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.style.display = 'none';
            addToCartTextError.removeAttribute('aria-live');
          }

          // Add aria-live region to inform screen readers that the item was added
          if (this.refs.addToCartButtonContainer?.refs.addToCartButton) {
            const addToCartButton = this.refs.addToCartButtonContainer.refs.addToCartButton;
            const addedTextElement = addToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, 5000);
          }

          this.dispatchEvent(
            new CartAddEvent(response, mainProductId.toString(), {
              source: 'product-form-component',
              itemCount: items.reduce((sum, item) => sum + item.quantity, 0),
              productId: this.dataset.productId,
              sections: response.sections,
            })
          );

          if (!this.dataset.isUpsell) {
            console.log('[Product Form] Dispatching cart:added event', {
              productId: this.dataset.productId,
              productHandle: this.dataset.productHandle || window.location.pathname.split('/').pop(),
              variantId: mainProductId.toString(),
              quantity: mainProductQuantity
            });
            document.dispatchEvent(new CustomEvent('cart:added', {
              detail: {
                productId: this.dataset.productId,
                productHandle: this.dataset.productHandle || window.location.pathname.split('/').pop(),
                variantId: mainProductId.toString(),
                quantity: mainProductQuantity,
                items: response.items,
                isUpsell: false
              }
            }));
          } else {
            console.log('[Product Form] Skipping cart:added event (is upsell)');
          }

          // Recheck cart state after successful add
          if (window.productVisibilityManager) {
            window.productVisibilityManager.checkCartState();
          }
        }
      })
      .catch((error) => {
        clearTimeout(timeoutId);

        // Handle different types of errors
        let errorMessage = 'Failed to add item to cart. ';

        if (error.name === 'AbortError') {
          errorMessage += 'Request timed out. Please check your connection and try again.';
        } else if (error.message.includes('NetworkError') || error.message.includes('Failed to fetch')) {
          errorMessage += 'Network error. Please check your connection.';
        } else if (error.message.includes('Invalid JSON')) {
          errorMessage += 'Server error. Please try again.';
        } else {
          errorMessage += error.message || 'Please try again.';
        }

        // Display error to user
        if (addToCartTextError) {
          addToCartTextError.classList.remove('hidden');
          addToCartTextError.style.removeProperty('display');
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = errorMessage;
          } else {
            addToCartTextError.insertAdjacentText('beforeend', errorMessage);
          }
        }

        window.dispatchEvent(new CartErrorEvent(this.id, errorMessage));
        // console.error('Cart API error:', error);
      })
      .finally(() => {
        // add more thing to do in here if needed.
        cartPerformance.measureFromEvent('add:user-action', event);
      });
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId, addToCartButtonContainer } = this.refs;

    const currentAddToCartButton = addToCartButtonContainer?.refs.addToCartButton;
    const newAddToCartButton = event.detail.data.html.querySelector('[ref="addToCartButton"]');

    if (!currentAddToCartButton) return;

    // Update the button state
    if (event.detail.resource == null || event.detail.resource.available == false) {
      currentAddToCartButton.setAttribute('disabled', 'true');
      this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
    } else {
      addToCartButtonContainer.refs.addToCartButton.removeAttribute('disabled');
      this.refs.acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
    }

    // Update the add to cart button text and icon
    if (newAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    // Update the variant ID
    variantId.value = event.detail.resource.id ?? '';

    // Set the data attribute for the add to cart button to the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      productVariantMedia &&
        addToCartButtonContainer?.setAttribute('data-product-variant-media', productVariantMedia + '&width=100');
    }
  };
}

if (!customElements.get('product-form-component')) {
  // console.log('Registering ProductFormComponent');
  customElements.define('product-form-component', ProductFormComponent);
} else {
  // console.log('ProductFormComponent already registered');
}
